import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  groupModelsByProvider,
  isModelId,
  ModelId,
  modelLabel,
  ProviderGroup,
  PublicModel,
  readStoredModelId,
  resolveModelId,
  savePreferredModel,
  usePublicModels,
} from '../services/modelCatalog';

/*
 * One model selector for every composer (landing, planning, chat): a trigger button that opens
 * a menu with the featured models and a "More models" entry whose submenu lists the rest of the
 * catalogue grouped by provider, with a search box. Controlled when `value`/`onChange` are
 * given; otherwise it keeps the user's preferred model in localStorage so all surfaces agree.
 */

export { DEFAULT_MODEL, MODEL_OPTIONS } from '../services/modelCatalog';

export const MORE_MODELS_VALUE = '__more__';

interface ModelSelectDropdownProps {
  disabled?: boolean;
  value?: ModelId;
  onChange?: (model: ModelId) => void;
  className?: string;
  /** Per-loop settings keep their choice separate from the shared composer default. */
  persistPreference?: boolean;
}

const chatReady = (model: PublicModel) => model.chat_available !== false;

const splitCatalogue = (models: PublicModel[]) => ({
  featured: models.filter((model) => model.featured && chatReady(model)),
  groups: groupModelsByProvider(models.filter((model) => !model.featured && chatReady(model))),
});

const filterGroups = (groups: ProviderGroup[], query: string): ProviderGroup[] => {
  const needle = query.trim().toLowerCase();

  if (!needle) return groups;

  return groups
    .map((group) => ({
      provider: group.provider,
      models: group.models.filter((model) => (
        `${model.name} ${model.id} ${model.provider}`.toLowerCase().includes(needle)
      )),
    }))
    .filter((group) => group.models.length > 0);
};

const useModelChoice = (value: ModelId | undefined, onChange?: (model: ModelId) => void, persistPreference = true) => {
  const { models } = usePublicModels();
  const [stored, setStored] = useState<string | null>(readStoredModelId);
  const current = value ?? resolveModelId(stored);
  const pick = useCallback((next: string) => {
    if (!next || next === MORE_MODELS_VALUE || !isModelId(next)) return false;
    if (persistPreference) savePreferredModel(next);
    setStored(next);
    onChange?.(next);

    return true;
  }, [onChange, persistPreference]);
  const { featured, groups } = useMemo(() => splitCatalogue(models), [models]);

  return { current, pick, featured, groups, models };
};

const useMenuState = (wrapRef: React.RefObject<HTMLDivElement | null>, disabled: boolean) => {
  const [open, setOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [query, setQuery] = useState('');
  const close = useCallback(() => { setOpen(false); setMoreOpen(false); setQuery(''); }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (event: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target as Node)) close();
    };
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') close(); };

    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);

    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [close, open, wrapRef]);

  return { open: open && !disabled, setOpen, moreOpen, setMoreOpen, query, setQuery, close };
};

interface Placement {
  vertical: 'up' | 'down';
  align: 'left' | 'right';
}

const MENU_WIDTH = 250;
const SUBMENU_WIDTH = 306;

/** Open upward near the bottom of the viewport (chat composer) and right-aligned near its right edge. */
const placementFor = (element: HTMLElement | null): Placement => {
  if (!element) return { vertical: 'down', align: 'left' };
  const rect = element.getBoundingClientRect();
  const vertical = window.innerHeight - rect.bottom < 360 && rect.top > 360 ? 'up' : 'down';
  const align = rect.left + MENU_WIDTH + SUBMENU_WIDTH > window.innerWidth ? 'right' : 'left';

  return { vertical, align };
};

const useSearchFocus = () => {
  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => { ref.current?.focus({ preventScroll: true }); }, []);

  return ref;
};

const Chevron: React.FC<{ direction?: 'down' | 'right' }> = ({ direction = 'down' }) => (
  <svg
    className={`model-picker__chevron model-picker__chevron--${direction}`}
    width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const ModelItem: React.FC<{
  model: PublicModel; selected: boolean; onPick: (id: string) => void; showProvider?: boolean;
}> = ({ model, selected, onPick, showProvider = false }) => (
  <li
    role="option"
    aria-selected={selected}
    data-model-id={model.id}
    className={`model-picker__item${selected ? ' model-picker__item--active' : ''}`}
    onClick={() => onPick(model.id)}
  >
    <span className="model-picker__check" aria-hidden="true">{selected ? '✓' : ''}</span>
    <span className="model-picker__name">{model.name}</span>
    {showProvider && <span className="model-picker__provider">{model.provider}</span>}
  </li>
);

const MoreModelsPanel: React.FC<{
  groups: ProviderGroup[]; current: string; query: string; onQuery: (q: string) => void; onPick: (id: string) => void;
}> = ({ groups, current, query, onQuery, onPick }) => {
  const visible = filterGroups(groups, query);
  const searchRef = useSearchFocus();

  return (
    <div className="model-picker__submenu" role="group" aria-label="More models">
      <input
        ref={searchRef}
        type="search"
        className="model-picker__search"
        placeholder="Search models…"
        aria-label="Search models"
        value={query}
        onChange={(event) => onQuery(event.target.value)}
      />
      <ul className="model-picker__list" role="listbox" aria-label="More models">
        {visible.map((group) => (
          <li key={group.provider} className="model-picker__group">
            <span className="model-picker__group-label">{group.provider}</span>
            <ul role="presentation">
              {group.models.map((model) => (
                <ModelItem key={model.id} model={model} selected={model.id === current} onPick={onPick} />
              ))}
            </ul>
          </li>
        ))}
        {visible.length === 0 && <li className="model-picker__empty">No models match</li>}
      </ul>
    </div>
  );
};

const Menu: React.FC<{
  featured: PublicModel[]; groups: ProviderGroup[]; current: string; placement: Placement;
  moreOpen: boolean; onToggleMore: () => void; query: string; onQuery: (q: string) => void;
  onPick: (id: string) => void;
}> = ({ featured, groups, current, placement, moreOpen, onToggleMore, query, onQuery, onPick }) => (
  <div
    className={`model-picker__menu model-picker__menu--${placement.vertical} model-picker__menu--${placement.align}`
      + (moreOpen ? ' has-submenu' : '')}
  >
    <ul className="model-picker__list" role="listbox" aria-label="Featured models">
      {featured.map((model) => (
        <ModelItem key={model.id} model={model} selected={model.id === current} onPick={onPick} showProvider />
      ))}
    </ul>
    {groups.length > 0 && (
      <button
        type="button"
        className={`model-picker__more${moreOpen ? ' is-open' : ''}`}
        aria-haspopup="listbox"
        aria-expanded={moreOpen}
        data-testid="model-picker-more"
        onClick={onToggleMore}
      >
        <span>More models</span>
        <span className="model-picker__more-count">{groups.reduce((n, g) => n + g.models.length, 0)}</span>
        <Chevron direction="right" />
      </button>
    )}
    {moreOpen && <MoreModelsPanel groups={groups} current={current} query={query} onQuery={onQuery} onPick={onPick} />}
  </div>
);

// eslint-disable-next-line max-lines-per-function, max-statements
export const ModelSelectDropdown: React.FC<ModelSelectDropdownProps> = ({
  disabled = false, value, onChange, className = '', persistPreference = true,
}) => {
  const wrapRef = useRef<HTMLDivElement>(null);
  const { current, pick, featured, groups, models } = useModelChoice(value, onChange, persistPreference);
  const menu = useMenuState(wrapRef, disabled);
  const [placement, setPlacement] = useState<Placement>({ vertical: 'down', align: 'left' });
  const currentModel = models.find((model) => model.id === current);
  const onPick = (id: string) => { if (pick(id)) menu.close(); };
  const openMore = !featured.some((model) => model.id === current) && groups.length > 0;

  return (
    <div
      ref={wrapRef}
      className={`model-select-dropdown model-picker ${className}`.trim()}
      onClick={(event) => event.stopPropagation()}
    >
      <button
        type="button"
        className="model-picker__trigger"
        aria-label="Model"
        aria-haspopup="listbox"
        aria-expanded={menu.open}
        disabled={disabled}
        title={`Model: ${modelLabel(current)}`}
        data-testid="model-picker-trigger"
        onClick={() => {
          if (menu.open) {
            menu.close();

            return;
          }
          setPlacement(placementFor(wrapRef.current));
          menu.setOpen(true);
          menu.setMoreOpen(openMore);
        }}
      >
        <span className="model-picker__label">{currentModel?.name ?? modelLabel(current)}</span>
        <Chevron />
      </button>
      {menu.open && (
        <Menu
          featured={featured}
          groups={groups}
          current={current}
          placement={placement}
          moreOpen={menu.moreOpen}
          onToggleMore={() => menu.setMoreOpen((open) => !open)}
          query={menu.query}
          onQuery={menu.setQuery}
          onPick={onPick}
        />
      )}
    </div>
  );
};
